import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import SessionsTable from '@/components/ui/SessionsTable';
import { faComments, faSpinner, faArrowLeft } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { ChatSession } from '@/models';

const pageSize = 50;

export default function AdminSessionsList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [criticalError, setCriticalError] = useState<boolean>(false);

  useEffect(() => {
    fetchSessions();
  }, [currentPage]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listAllChatSessions({
        limit: pageSize,
        next: cursorToken
      });
      setSessions(response.sessions);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading sessions list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Chat-Sitzungen');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Chat-Sitzungen nicht verfügbar"
        message={error || 'Die Chat-Sitzungen konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchSessions}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Chat-Sitzungen ({total})
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Action buttons can be added here */}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {sessions.length > 0 && !loading && (
          <SessionsTable sessions={sessions} showUserColumn={true}>
            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </SessionsTable>
        )}

        {sessions.length === 0 && !loading && !error && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faComments} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Keine Chat-Sitzungen gefunden</p>
          </div>
        )}
    </>
  );
}
